import { useState } from "react";
import {
  Avatar,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Grid2,
  IconButton,
  Typography,
  useTheme
} from "@mui/material";
import { Badge, Desk, Edit, ExpandMore, Group, SupervisorAccount } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { StyledReportCardRoot } from "./partial/StyledReportCard.partial";
import { PermissionAssignmentWithEntityId } from "./Domain/department-monitoring.types";
import { MonitoringReportCardSubHeader } from "./monitoring-report-card-subheader.partial";
import {
  LabeledLinearProgress,
  LabeledOverflowLinearProgress
} from "./partial/LabeledLinearProgress.partial";
import {
  PermissionAssignmentInfoDto,
  useRemotePostMonitoringQuota
} from "../../../hooks/Remote/Capacity/DepartmentMonitoring/useRemotePostMonitoringQuota";
import { MonitoringQuotaEditDialog } from "./monitoring-quota-edit-dialog.component";

export function MonitoringReportCard({
  data,
  refetchData
}: Readonly<{
  data: PermissionAssignmentWithEntityId;
  refetchData: () => void;
}>) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isExpanded, setIsExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const { mutateAsync: postMonitoringQuota } = useRemotePostMonitoringQuota();

  const handleEditQuota = (dto: Omit<PermissionAssignmentInfoDto, "costCenterUnitIds">) => {
    postMonitoringQuota({
      costCenterUnitIds: [data.departmentId],
      targetEmployeeQuota: dto.targetEmployeeQuota,
      targetSupervisorQuota: dto.targetSupervisorQuota
    }).then(() => refetchData());
  };

  return (
    <>
      <StyledReportCardRoot key={data.departmentId}>
        <CardHeader
          avatar={
            <Avatar alt={data.departmentName} aria-label="department-monitoring-avatar">
              <Group />
            </Avatar>
          }
          title={
            <Grid2 container alignItems={"center"} sx={{ pb: 1.5 }}>
              <Typography variant="h6">{data.departmentName}</Typography>
            </Grid2>
          }
          action={
            <Grid2
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
                height: 120
              }}
            >
              <IconButton
                sx={[
                  {
                    transition: theme.transitions.create("transform", {
                      duration: theme.transitions.duration.shortest
                    })
                  },
                  isExpanded ? { transform: "rotate(0deg)" } : { transform: "rotate(180deg)" }
                ]}
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                data-testid="department-monitoring-expand-btn"
                size="large"
              >
                <ExpandMore />
              </IconButton>

              <IconButton
                size="large"
                onClick={() => setEditOpen(true)}
                data-testid="change-quota-btn"
              >
                <Edit />
              </IconButton>
            </Grid2>
          }
          subheader={
            <Grid2
              container
              sx={{ width: "100%", alignItems: "center", justifyContent: "flex-start" }}
              data-testid="department-monitoring-card-subheader"
            >
              <MonitoringReportCardSubHeader
                type={"employee"}
                data={{
                  numberOfEntity: data.numberOfEmployees,
                  numberOfParticipatingInMobileWorking:
                    data.numberOfEmployeesParticipatingInMobileWorking,
                  targetQuota: data.targetEmployeeQuota,
                  permittedWorkplaces: data.permittedWorkplacesForEmployees,
                  resultingQuota: data.resultingEmployeeQuota
                }}
              />
              <MonitoringReportCardSubHeader
                type={"supervisor"}
                data={{
                  numberOfEntity: data.numberOfSupervisors,
                  numberOfParticipatingInMobileWorking:
                    data.numberOfSupervisorsParticipatingInMobileWorking,
                  targetQuota: data.targetSupervisorQuota,
                  permittedWorkplaces: data.permittedWorkplacesForSupervisors,
                  resultingQuota: data.resultingSupervisorQuota
                }}
              />
            </Grid2>
          }
        />

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent key={data.departmentId} sx={{ p: "0px 16px 16px 16px" }}>
            <Divider
              sx={{
                margin: "0 0 16px 0",
                opacity: "0.6"
              }}
            />
            <Grid2 container>
              <Grid2
                container
                sx={{ display: "flex", flexDirection: "column", justifyContent: "space-around" }}
                data-testid="monitoring-report-detail-container"
              >
                {/* employee - upper box */}
                <Grid2 data-testid="monitoring-report-detail-employee-container">
                  <Grid2
                    container
                    sx={{ display: "flex", gap: 0.5, justifyContent: "space-between" }}
                  >
                    <Grid2 sx={{ display: "flex", gap: 0.5 }}>
                      <Badge />
                      <Typography>{t("Employee")}</Typography>
                    </Grid2>
                    <Grid2 sx={{ display: "flex", alignItems: "flex-end" }}>
                      <Desk sx={{ fontSize: "1.15rem", mr: 0.5 }} />
                      <Typography variant={"body2"}>
                        {data.permittedWorkplacesForEmployees}
                      </Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 sx={{ p: 1 }}>
                    <LabeledLinearProgress
                      title={t("Number of Employees")}
                      max={data.numberOfEmployees}
                      value={data.numberOfEmployeesParticipatingInMobileWorking}
                      maxLabel={t("Total")}
                      valueLabel={t("Mobile Working Participants")}
                    />
                    <LabeledOverflowLinearProgress
                      title={t("Quota")}
                      max={data.targetEmployeeQuota}
                      value={data.resultingEmployeeQuota}
                      maxLabel={t("TargetQuota")}
                      valueLabel={t("Current")}
                      showWarning={data.resultingEmployeeQuota <= data.targetEmployeeQuota}
                    />
                  </Grid2>
                </Grid2>
                <Divider
                  sx={{
                    margin: "0 0 16px 0",
                    opacity: "0.6"
                  }}
                />
                {/* supervisor - lower box */}
                <Grid2 data-testid="monitoring-report-detail-supervisor-container">
                  <Grid2
                    container
                    sx={{ display: "flex", gap: 0.5, justifyContent: "space-between" }}
                  >
                    <Grid2 sx={{ display: "flex", gap: 0.5 }}>
                      <SupervisorAccount />
                      <Typography>{t("Supervisor")}</Typography>
                    </Grid2>

                    <Grid2 sx={{ display: "flex", alignItems: "flex-end" }}>
                      <Desk sx={{ fontSize: "1.15rem", mr: 0.5 }} />
                      <Typography variant={"body2"}>
                        {data.permittedWorkplacesForSupervisors}
                      </Typography>
                    </Grid2>
                  </Grid2>

                  <Grid2 sx={{ p: 1 }}>
                    <LabeledLinearProgress
                      title={t("Number of Supervisors")}
                      max={data.numberOfSupervisors}
                      value={data.numberOfSupervisorsParticipatingInMobileWorking}
                      maxLabel={t("Total")}
                      valueLabel={t("Mobile Working Participants")}
                    />
                    <LabeledOverflowLinearProgress
                      title={t("Quota")}
                      max={data.targetSupervisorQuota}
                      value={data.resultingSupervisorQuota}
                      maxLabel={t("TargetQuota")}
                      valueLabel={t("Current")}
                      showWarning={data.resultingSupervisorQuota <= data.targetSupervisorQuota}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>
            </Grid2>
          </CardContent>
        </Collapse>
      </StyledReportCardRoot>

      <MonitoringQuotaEditDialog
        data={data}
        isOpen={editOpen}
        setEditOpen={setEditOpen}
        handleEditQuota={dto => handleEditQuota(dto)}
      />
    </>
  );
}
